import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Scissors, 
  User, 
  Building2, 
  MapPin, 
  Mail, 
  Phone,
  Lock,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  Loader2,
  Upload,
  AlertCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Service types available
const SERVICE_TYPES = [
  { id: 'alterations', name: 'Alterations & Repairs' },
  { id: 'tailoring', name: 'Custom Tailoring' },
  { id: 'wedding', name: 'Wedding & Bridal' },
  { id: 'saree-blouse', name: 'Saree Blouse Stitching' },
  { id: 'embroidery', name: 'Embroidery & Embellishments' },
  { id: 'dry-cleaning', name: 'Dry Cleaning' },
];

// Specializations
const SPECIALIZATIONS = [
  'Wedding Dresses', 'Evening Wear', 'Office Attire', 'Casual Wear',
  'Bridal Wear', 'Traditional', 'Designer Blouses', 'Kids Clothing',
  'Suits & Blazers', 'Denim Alterations', 'Leather Work', 'Embroidery',
];

const ProviderRegister = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [providerId, setProviderId] = useState(null);
  
  const [formData, setFormData] = useState({
    // Step 1: Personal Info
    full_name: '',
    email: '',
    phone: '',
    password: '',
    confirm_password: '',
    
    // Step 2: Business Info
    business_name: '',
    business_type: 'individual',
    service_type: '',
    years_experience: 0,
    
    // Step 3: Location
    address: '',
    city: '',
    country: 'SL',
    
    // Step 4: Services
    service_description: '',
    specializations: [],
  });

  const updateField = (field, value) => {
    setFormData({ ...formData, [field]: value });
    setError('');
  };

  const toggleSpecialization = (spec) => {
    const current = formData.specializations;
    const newSpecs = current.includes(spec)
      ? current.filter(s => s !== spec)
      : [...current, spec];
    updateField('specializations', newSpecs);
  };

  const validateStep = () => {
    switch (step) {
      case 1:
        if (!formData.full_name || !formData.email || !formData.phone || !formData.password) {
          setError('Please fill all required fields');
          return false;
        }
        if (formData.password !== formData.confirm_password) {
          setError('Passwords do not match');
          return false;
        }
        if (formData.password.length < 6) {
          setError('Password must be at least 6 characters');
          return false;
        }
        break;
      case 2:
        if (!formData.business_name || !formData.service_type) {
          setError('Please fill all required fields');
          return false;
        }
        break;
      case 3:
        if (!formData.address || !formData.city) {
          setError('Please fill all required fields');
          return false;
        }
        break;
      case 4:
        if (!formData.service_description) {
          setError('Please describe your services');
          return false;
        }
        break;
      default:
        break;
    }
    return true;
  };

  const handleNext = () => {
    if (validateStep()) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    setStep(step - 1);
    setError('');
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    
    setLoading(true);
    setError('');
    
    try {
      const response = await axios.post(`${API_URL}/api/provider/register`, {
        full_name: formData.full_name,
        email: formData.email,
        phone: formData.phone,
        password: formData.password,
        business_name: formData.business_name,
        business_type: formData.business_type,
        service_type: formData.service_type,
        address: formData.address,
        city: formData.city,
        country: formData.country,
        service_description: formData.service_description,
        years_experience: formData.years_experience,
        specializations: formData.specializations,
      });
      
      setProviderId(response.data.provider_id);
      setSuccess(true);
      setStep(5);
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-rose-50 py-8 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 mb-4">
            <Scissors className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Service Provider Registration</h1>
          <p className="text-gray-600 mt-2">Join our marketplace and grow your business</p>
        </div>

        {/* Progress Steps */}
        {step <= 4 && (
          <div className="flex items-center justify-center gap-2 mb-8">
            {['Personal', 'Business', 'Location', 'Services'].map((label, index) => (
              <div key={label} className="flex items-center">
                <div className={`flex items-center justify-center w-8 h-8 rounded-full text-sm font-medium ${
                  step > index + 1
                    ? 'bg-green-500 text-white'
                    : step === index + 1
                    ? 'bg-purple-600 text-white'
                    : 'bg-gray-200 text-gray-500'
                }`}>
                  {step > index + 1 ? <CheckCircle2 className="h-5 w-5" /> : index + 1}
                </div>
                {index < 3 && (
                  <div className={`w-8 h-1 mx-1 ${
                    step > index + 1 ? 'bg-green-500' : 'bg-gray-200'
                  }`} />
                )}
              </div>
            ))}
          </div>
        )}

        <Card className="shadow-lg">
          <CardContent className="p-6">
            {/* Error Message */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-700">
                <AlertCircle className="h-5 w-5" />
                <span>{error}</span>
              </div>
            )}

            {/* Step 1: Personal Info */}
            {step === 1 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <User className="h-5 w-5 text-purple-600" />
                  <h2 className="text-lg font-semibold">Personal Information</h2>
                </div>
                
                <div>
                  <Label htmlFor="full_name">Full Name *</Label>
                  <Input
                    id="full_name"
                    value={formData.full_name}
                    onChange={(e) => updateField('full_name', e.target.value)}
                    placeholder="Enter your full name"
                    data-testid="provider-full-name"
                  />
                </div>
                
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => updateField('email', e.target.value)}
                    placeholder="your@email.com"
                    data-testid="provider-email"
                  />
                </div>
                
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    value={formData.phone}
                    onChange={(e) => updateField('phone', e.target.value)}
                    placeholder="07X XXX XXXX"
                    data-testid="provider-phone"
                  />
                </div>
                
                <div>
                  <Label htmlFor="password">Password *</Label>
                  <Input
                    id="password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => updateField('password', e.target.value)}
                    placeholder="At least 6 characters"
                    data-testid="provider-password"
                  />
                </div>
                
                <div>
                  <Label htmlFor="confirm_password">Confirm Password *</Label>
                  <Input
                    id="confirm_password"
                    type="password"
                    value={formData.confirm_password}
                    onChange={(e) => updateField('confirm_password', e.target.value)}
                    placeholder="Re-enter password"
                    data-testid="provider-confirm-password"
                  />
                </div>
              </div>
            )}

            {/* Step 2: Business Info */}
            {step === 2 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Building2 className="h-5 w-5 text-purple-600" />
                  <h2 className="text-lg font-semibold">Business Information</h2>
                </div>
                
                <div>
                  <Label htmlFor="business_name">Business Name *</Label>
                  <Input
                    id="business_name"
                    value={formData.business_name}
                    onChange={(e) => updateField('business_name', e.target.value)}
                    placeholder="Your business or brand name"
                    data-testid="provider-business-name"
                  />
                </div>
                
                <div>
                  <Label>Business Type *</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {[
                      { id: 'individual', label: 'Individual / Freelancer' },
                      { id: 'company', label: 'Registered Business' },
                    ].map((type) => (
                      <div
                        key={type.id}
                        className={`p-3 border-2 rounded-lg cursor-pointer transition-all text-center ${
                          formData.business_type === type.id
                            ? 'border-purple-500 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300'
                        }`}
                        onClick={() => updateField('business_type', type.id)}
                      >
                        <span className="text-sm font-medium">{type.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
                <div>
                  <Label>Service Type *</Label>
                  <Select 
                    value={formData.service_type}
                    onValueChange={(value) => updateField('service_type', value)}
                  >
                    <SelectTrigger data-testid="provider-service-type">
                      <SelectValue placeholder="Select your primary service" />
                    </SelectTrigger>
                    <SelectContent>
                      {SERVICE_TYPES.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="years_experience">Years of Experience</Label>
                  <Input
                    id="years_experience"
                    type="number"
                    min={0}
                    max={50}
                    value={formData.years_experience}
                    onChange={(e) => updateField('years_experience', parseInt(e.target.value) || 0)}
                    placeholder="0"
                  />
                </div>
              </div>
            )}

            {/* Step 3: Location */}
            {step === 3 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="h-5 w-5 text-purple-600" />
                  <h2 className="text-lg font-semibold">Business Location</h2>
                </div>
                
                <div>
                  <Label htmlFor="address">Business Address *</Label>
                  <Textarea
                    id="address"
                    value={formData.address}
                    onChange={(e) => updateField('address', e.target.value)}
                    placeholder="Enter your business address"
                    rows={2}
                    data-testid="provider-address"
                  />
                </div>
                
                <div>
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    value={formData.city}
                    onChange={(e) => updateField('city', e.target.value)}
                    placeholder="e.g., Colombo"
                    data-testid="provider-city"
                  />
                </div>
                
                <div>
                  <Label>Country</Label>
                  <Select 
                    value={formData.country}
                    onValueChange={(value) => updateField('country', value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SL">🇱🇰 Sri Lanka</SelectItem>
                      <SelectItem value="UK">🇬🇧 United Kingdom</SelectItem>
                      <SelectItem value="US">🇺🇸 United States</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 4: Services */}
            {step === 4 && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 mb-4">
                  <Scissors className="h-5 w-5 text-purple-600" />
                  <h2 className="text-lg font-semibold">Your Services</h2>
                </div>
                
                <div>
                  <Label htmlFor="service_description">Describe Your Services *</Label>
                  <Textarea
                    id="service_description"
                    value={formData.service_description}
                    onChange={(e) => updateField('service_description', e.target.value)}
                    placeholder="Tell customers about your services, experience, and what makes you special..."
                    rows={4}
                    data-testid="provider-description"
                  />
                </div>
                
                <div>
                  <Label>Specializations (Select all that apply)</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {SPECIALIZATIONS.map((spec) => (
                      <Badge
                        key={spec}
                        variant={formData.specializations.includes(spec) ? 'default' : 'outline'}
                        className={`cursor-pointer ${
                          formData.specializations.includes(spec)
                            ? 'bg-purple-600'
                            : 'hover:bg-purple-50'
                        }`}
                        onClick={() => toggleSpecialization(spec)}
                      >
                        {spec}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 5: Success */}
            {step === 5 && success && (
              <div className="text-center py-6">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900 mb-2">Registration Successful!</h2>
                <p className="text-gray-600 mb-6">
                  Your account has been created. Please upload verification documents to complete your profile.
                </p>
                
                <div className="p-4 bg-purple-50 rounded-lg mb-6">
                  <h3 className="font-medium text-purple-900 mb-2">Next Steps:</h3>
                  <ol className="text-sm text-purple-800 text-left space-y-2">
                    <li>1. Login to your dashboard</li>
                    <li>2. Upload verification documents (ID, business registration)</li>
                    <li>3. Add your service pricing</li>
                    <li>4. Wait for admin approval (usually 24-48 hours)</li>
                  </ol>
                </div>
                
                <Button 
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  onClick={() => navigate('/provider/login')}
                  data-testid="go-to-login"
                >
                  Go to Login
                  <ChevronRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            )}

            {/* Navigation Buttons */}
            {step <= 4 && (
              <div className="flex justify-between mt-6 pt-4 border-t">
                {step > 1 ? (
                  <Button variant="outline" onClick={handleBack}>
                    <ChevronLeft className="h-4 w-4 mr-2" />
                    Back
                  </Button>
                ) : (
                  <Link to="/provider/login">
                    <Button variant="ghost">
                      Already have an account?
                    </Button>
                  </Link>
                )}
                
                {step < 4 ? (
                  <Button 
                    className="bg-purple-600 hover:bg-purple-700"
                    onClick={handleNext}
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                ) : (
                  <Button 
                    className="bg-green-600 hover:bg-green-700"
                    onClick={handleSubmit}
                    disabled={loading}
                    data-testid="submit-registration"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Registering...
                      </>
                    ) : (
                      <>
                        Complete Registration
                        <CheckCircle2 className="h-4 w-4 ml-2" />
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ProviderRegister;
